import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import "./CourseContent.css";

// "HH:MM:SS" from the API -> seconds.
const toSeconds = (duration) => {
    if (!duration) return 0;
    return String(duration).split(':').reduce((total, part) => total * 60 + (Number(part) || 0), 0);
};

// Lesson length as a clock: 10:06, 1:02:30.
const formatClock = (seconds) => {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = seconds % 60;
    const pad = (n) => String(n).padStart(2, '0');
    return h > 0 ? `${h}:${pad(m)}:${pad(s)}` : `${m}:${pad(s)}`;
};

export default function CourseContent(props) {
    const { t } = useTranslation();
    const sections = props?.data?.sections || [];
    const [open, setOpen] = useState(() => new Set(sections.length ? [sections[0].id] : []));

    // Open the first section once the course has loaded.
    useEffect(() => {
        if (sections.length) setOpen(new Set([sections[0].id]));
    }, [props?.data?.id]); // eslint-disable-line react-hooks/exhaustive-deps

    const sectionSeconds = (section) => (section?.lessons || []).reduce((sum, lesson) => sum + toSeconds(lesson?.duration), 0);
    const totalSeconds = sections.reduce((sum, section) => sum + sectionSeconds(section), 0);
    const lectureCount = sections.reduce((sum, section) => sum + (section?.lessons?.length || 0), 0);
    const allOpen = sections.length > 0 && sections.every((section) => open.has(section.id));

    // Section / course length in words: "11 min", "1 hr 5 min".
    const formatLength = (seconds) => {
        const minutes = Math.round(seconds / 60);
        const h = Math.floor(minutes / 60);
        const m = minutes % 60;
        if (h === 0) return t("courseContent.length_m", { m });
        return m === 0 ? t("courseContent.length_h", { h }) : t("courseContent.length_hm", { h, m });
    };

    const toggle = (id) => setOpen((prev) => {
        const next = new Set(prev);
        next.has(id) ? next.delete(id) : next.add(id);
        return next;
    });
    const toggleAll = () => setOpen(allOpen ? new Set() : new Set(sections.map((section) => section.id)));

    return (
        <div className="cc">
            <div className="cc-head">
                <h2 className="cc-title">{t("courseContent.course_content")}</h2>
                <div className="cc-summary">
                    <span>
                        {t("courseContent.sections_count", { count: sections.length })}
                        <span className="cc-dot">•</span>
                        {t("courseContent.lectures_count", { count: lectureCount })}
                        {totalSeconds > 0 && <>
                            <span className="cc-dot">•</span>
                            {t("courseContent.total_length", { length: formatLength(totalSeconds) })}
                        </>}
                    </span>
                    {sections.length > 1 &&
                        <button type="button" className="cc-toggle-all" onClick={toggleAll}>
                            {allOpen ? t("courseContent.collapse_all") : t("courseContent.expand_all")}
                        </button>
                    }
                </div>
            </div>

            {sections.length > 0 &&
                <div className="cc-list">
                    {sections.map((section, i) => {
                        const isOpen = open.has(section.id);
                        const lessons = section?.lessons || [];
                        const seconds = sectionSeconds(section);
                        const panelId = `cc-section-${section.id ?? i}`;
                        return (
                            <div className={`cc-section ${isOpen ? 'is-open' : ''}`} key={section.id ?? i}>
                                <button
                                    type="button"
                                    className="cc-section-head"
                                    aria-expanded={isOpen}
                                    aria-controls={panelId}
                                    onClick={() => toggle(section.id)}
                                >
                                    <i className="fa-light fa-chevron-down cc-chevron" />
                                    <span className="cc-section-name">{section.name}</span>
                                    <span className="cc-section-meta">
                                        {t("courseContent.lectures_count", { count: lessons.length })}
                                        {seconds > 0 && <><span className="cc-dot">•</span>{formatLength(seconds)}</>}
                                    </span>
                                </button>

                                {isOpen &&
                                    <ul className="cc-lessons" id={panelId}>
                                        {lessons.length === 0 &&
                                            <li className="cc-empty">{t("courseContent.no_lectures")}</li>
                                        }
                                        {lessons.map((lesson, k) => {
                                            const isPreview = Number(lesson?.is_trial) === 1;
                                            const lessonSeconds = toSeconds(lesson?.duration);
                                            return (
                                                <li className="cc-lesson" key={lesson.id ?? k}>
                                                    <i className={`fa-light ${lesson?.lesson_type === 'live' ? 'fa-video' : 'fa-circle-play'} cc-lesson-icon`} />
                                                    <span className="cc-lesson-title">{lesson.title}</span>
                                                    {isPreview && <span className="cc-preview">{t("courseContent.preview")}</span>}
                                                    {lessonSeconds > 0 && <span className="cc-lesson-time">{formatClock(lessonSeconds)}</span>}
                                                </li>
                                            );
                                        })}
                                    </ul>
                                }
                            </div>
                        );
                    })}
                </div>
            }
        </div>
    )
}
